import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ChevronRight, Loader2, FileText, ThumbsUp, ThumbsDown, Eye, Calendar, Check } from 'lucide-react';
import { knowledgeService } from '@/services/knowledge.service';
import { formatDistanceToNow } from 'date-fns';
import { useEffect, useState } from 'react';
// H-1: imported by SUBPATH, not from the '@centraoneplus/ui' barrel.
// packages/ui is a source-only library, so importing its index pulls every
// component into this app's type program - and under `noUnusedLocals` that
// surfaced 43 latent errors in files this app never uses. apps/web already
// imports the barrel so it is unaffected; customer-portal did not.
import { sanitizeArticleHtml } from '@centraoneplus/ui/src/security/sanitize-html';

/**
 * Strip anything executable from stored article HTML before it is injected via
 * dangerouslySetInnerHTML. Published articles render in this PUBLIC portal, so
 * unsanitised author content is a stored-XSS vector (bug #9158): remove
 * script/style/embeds, inline event handlers (on*), and javascript: URLs while
 * preserving normal article formatting. Mirrors the sanitizer used for ticket
 * message bodies in the agent app. DOMParser is browser-only, so SSR/no-window
 * falls back to returning the raw string (this SPA always renders in a browser).
 */

export function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const { data: article, isLoading } = useQuery({
    queryKey: ['article', slug],
    queryFn: () => knowledgeService.getArticleBySlug(slug!),
    enabled: !!slug,
  });

  const { data: breadcrumb } = useQuery({
    queryKey: ['category-breadcrumb', article?.categoryId],
    queryFn: () => knowledgeService.getCategoryBreadcrumb(article!.categoryId!),
    enabled: !!article?.categoryId,
  });

  const { data: relatedArticles } = useQuery({
    queryKey: ['related-articles', article?.id],
    queryFn: () => knowledgeService.getRelatedArticles(article!.id, 5),
    enabled: !!article?.id,
  });

  const feedbackMutation = useMutation({
    mutationFn: ({ isHelpful }: { isHelpful: boolean }) =>
      knowledgeService.submitArticleFeedback(article!.id, isHelpful),
    onSuccess: () => {
      setFeedbackSubmitted(true);
    },
  });

  // Record view on mount
  useEffect(() => {
    if (article?.id) {
      knowledgeService.recordArticleView(article.id).catch(() => {
        // Silently fail - view tracking is not critical
      });
    }
  }, [article?.id]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="container py-16 text-center">
        <FileText className="h-16 w-16 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Article Not Found</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">The article you're looking for doesn't exist.</p>
        <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
          Back to Home
        </Link>
      </div>
    );
  }

  const publishedDate = article.publishedAt
    ? formatDistanceToNow(new Date(article.publishedAt), { addSuffix: true })
    : null;

  return (
    <div className="py-8">
      <div className="container">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Breadcrumb */}
            {breadcrumb && (
              <nav className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-6 flex-wrap">
                <Link to="/" className="hover:text-primary-600">
                  Home
                </Link>
                {breadcrumb.map((item) => (
                  <span key={item.id} className="flex items-center gap-2">
                    <ChevronRight className="h-4 w-4" />
                    <Link to={`/category/${item.slug}`} className="hover:text-primary-600">
                      {item.name}
                    </Link>
                  </span>
                ))}
                <span className="flex items-center gap-2">
                  <ChevronRight className="h-4 w-4" />
                  <span className="text-gray-900 dark:text-white font-medium truncate">{article.title}</span>
                </span>
              </nav>
            )}

            {/* Article Content */}
            <article className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-8">
              <header className="mb-6 pb-6 border-b border-gray-100 dark:border-gray-700">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{article.title}</h1>
                <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                  {publishedDate && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      Updated {publishedDate}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Eye className="h-4 w-4" />
                    {article.viewCount} views
                  </span>
                </div>
              </header>

              {/* Article Body */}
              {article.contentHtml ? (
                <div
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(article.contentHtml) }}
                />
              ) : (
                <div className="prose max-w-none whitespace-pre-wrap">{article.content}</div>
              )}

              {/* Tags */}
              {article.tags && article.tags.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex flex-wrap gap-2">
                    {article.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/search?q=${encodeURIComponent(tag)}`}
                        className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full text-sm hover:bg-gray-200 transition-colors"
                      >
                        {tag}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Feedback */}
              <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300 font-medium mb-4">Was this article helpful?</p>
                {feedbackSubmitted ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <Check className="h-5 w-5" />
                    <span>Thank you for your feedback!</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => feedbackMutation.mutate({ isHelpful: true })}
                      disabled={feedbackMutation.isPending}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-green-50 hover:border-green-300 hover:text-green-700 transition-colors disabled:opacity-50"
                    >
                      <ThumbsUp className="h-4 w-4" />
                      Yes
                    </button>
                    <button
                      onClick={() => feedbackMutation.mutate({ isHelpful: false })}
                      disabled={feedbackMutation.isPending}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-colors disabled:opacity-50"
                    >
                      <ThumbsDown className="h-4 w-4" />
                      No
                    </button>
                  </div>
                )}
              </div>
            </article>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Related Articles */}
            {relatedArticles && relatedArticles.length > 0 && (
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Related Articles</h3>
                <div className="space-y-3">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.slug}`}
                      className="block text-sm text-gray-700 dark:text-gray-300 hover:text-primary-600 transition-colors line-clamp-2"
                    >
                      {related.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Need More Help */}
            <div className="bg-primary-50 dark:bg-gray-800 rounded-xl p-6 mt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Need more help?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Can't find what you're looking for? Our support team is here to help.
              </p>
              <Link
                to="/submit-ticket"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                Submit a Request
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
